import type {
  AddressCreateFromGooglePlaceIdRecord,
  AddressForBookingOutputRecord,
  AddressRecoveryRecord
} from '@navio/nsdk';
import { AddressRecoveryReason } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { parseAddressRecovery } from './addressRecovery';

export type AddressCompletion = Pick<AddressCreateFromGooglePlaceIdRecord, 'streetNumber' | 'zipCode' | 'city'>;

type Props = {
  onSuccess: (address: AddressForBookingOutputRecord) => void;
  onError?: () => void;
  onResolutionChange?: (pending: boolean) => void;
};

export const useAddressResolution = ({ onSuccess, onError, onResolutionChange }: Props) => {
  const [recovery, setRecovery] = useState<AddressRecoveryRecord | null>(null);
  const current = useRef<AddressCreateFromGooglePlaceIdRecord | null>(null);
  const releasePending = useEffectEvent(() => onResolutionChange?.(false));
  useEffect(
    () => () => {
      current.current = null;
      releasePending();
    },
    []
  );
  const mutation = noddiAsync.usePost({
    type: URLKeys.postAddressCreatedFromPlaceId,
    queryConfig: {
      meta: { suppressErrorToast: true, suppressSuccessToast: true },
      onSuccess: ({ data }, variables) => {
        if (current.current === variables) {
          setRecovery(null);
          onSuccess(data);
        }
      },
      onError: (error, variables) => {
        if (current.current === variables) {
          const parsed = parseAddressRecovery(error);
          setRecovery((previous) => {
            if (previous && !parsed?.missingFields.length) {
              return { ...previous, reason: parsed?.reason ?? AddressRecoveryReason.UNAVAILABLE };
            }
            return parsed;
          });
          onError?.();
        }
      },
      onSettled: (_data, _error, variables) => {
        if (current.current === variables) {
          onResolutionChange?.(false);
        }
      }
    }
  });
  const send = (input: AddressCreateFromGooglePlaceIdRecord) => {
    const request = { ...input };
    current.current = request;
    onResolutionChange?.(true);
    mutation.mutate(request);
  };
  const resolve = (input: AddressCreateFromGooglePlaceIdRecord) => {
    setRecovery(null);
    send(input);
  };
  const clear = () => {
    current.current = null;
    setRecovery(null);
    onResolutionChange?.(false);
    mutation.reset();
  };
  const complete = (completion: AddressCompletion) => {
    if (current.current) {
      send({ ...current.current, ...completion });
    }
  };
  const completionSubmitted = Boolean(
    mutation.variables?.streetNumber || mutation.variables?.zipCode || mutation.variables?.city
  );
  return {
    resolve,
    clear,
    error: mutation.error,
    data: mutation.data,
    isPending: mutation.isPending,
    recoveryFormProps: recovery
      ? {
          recovery,
          loading: mutation.isPending,
          onSubmit: complete,
          onRetry: () => {
            if (current.current) {
              send(current.current);
            }
          },
          onCancel: clear,
          failed:
            Boolean(mutation.error) && (completionSubmitted || recovery.reason === AddressRecoveryReason.UNAVAILABLE)
        }
      : null
  };
};
